import { useEffect, useReducer, useCallback, useState, useRef } from 'react';
import { useSocket } from '../context/SocketContext';
import {
  SERVER_EVENTS,
  CLIENT_EVENTS,
  LobbySnapshot,
  PlayerSnapshot,
  SpinStartedPayload,
  LobbyJoinedPayload,
  PlayerUpdatedPayload,
  PlayerLeftPayload,
  GameResetPayload,
  AdminTransferredPayload,
  ErrorPayload,
  MinigameStartedPayload,
  MinigameAnswerReceivedPayload,
  MinigameResultsPayload,
  MinigameEndedPayload,
  MinigameStateSnapshot,
  MinigameType,
} from '../shared/events';
import { LobbyState, Player } from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

type LobbyAction =
  | { type: 'LOBBY_JOINED'; lobby: LobbySnapshot }
  | { type: 'PLAYER_UPDATED'; player: PlayerSnapshot }
  | { type: 'PLAYER_LEFT'; playerId: string }
  | { type: 'SPIN_STARTED'; winner: PlayerSnapshot; finalAngle: number; duration: number }
  | { type: 'SPIN_COMPLETE' }
  | { type: 'GAME_RESET'; lobby: LobbySnapshot }
  | { type: 'ADMIN_TRANSFERRED'; newAdminId: string }
  | { type: 'SET_PHASE'; phase: LobbyState['phase'] }
  | { type: 'MINIGAME_STARTED'; minigame: MinigameStateSnapshot }
  | { type: 'MINIGAME_ANSWER_COUNT'; answeredCount: number; totalCount: number }
  | { type: 'MINIGAME_RESULTS'; minigame: MinigameStateSnapshot; updatedPlayers: PlayerSnapshot[] }
  | { type: 'MINIGAME_ENDED'; lobby: LobbySnapshot }
  | { type: 'ERROR'; code: string; message: string };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function snapshotToPlayer(p: PlayerSnapshot): Player {
  return {
    id: p.id, name: p.name, avatarType: p.avatarType, avatar: p.avatar,
    coefficient: p.coefficient, color: p.color, isAdmin: p.isAdmin,
    isConnected: p.isConnected,
  };
}

function snapshotToLobby(snap: LobbySnapshot): LobbyState {
  return {
    id: snap.id,
    adminId: snap.adminId,
    players: snap.players.map(snapshotToPlayer),
    phase: snap.phase,
    winner: snap.winner ? snapshotToPlayer(snap.winner) : null,
    spinConfig: null,
    minigameState: snap.minigameState,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

function lobbyReducer(state: LobbyState | null, action: LobbyAction): LobbyState | null {
  switch (action.type) {
    case 'LOBBY_JOINED':
      return snapshotToLobby(action.lobby);

    case 'PLAYER_UPDATED': {
      if (!state) return state;
      const updated = snapshotToPlayer(action.player);
      const exists = state.players.some(p => p.id === updated.id);
      const players = exists
        ? state.players.map(p => (p.id === updated.id ? updated : p))
        : [...state.players, updated];
      return { ...state, players };
    }

    case 'PLAYER_LEFT': {
      if (!state) return state;
      return { ...state, players: state.players.filter(p => p.id !== action.playerId) };
    }

    case 'SPIN_STARTED': {
      if (!state) return state;
      return {
        ...state, phase: 'spinning',
        winner: snapshotToPlayer(action.winner),
        spinConfig: { finalAngle: action.finalAngle, duration: action.duration },
      };
    }

    case 'SPIN_COMPLETE': {
      if (!state) return state;
      return { ...state, phase: 'result' };
    }

    case 'GAME_RESET':
      return { ...snapshotToLobby(action.lobby), phase: 'waiting', winner: null, spinConfig: null };

    case 'ADMIN_TRANSFERRED': {
      if (!state) return state;
      return {
        ...state,
        adminId: action.newAdminId,
        players: state.players.map(p => ({ ...p, isAdmin: p.id === action.newAdminId })),
      };
    }

    case 'SET_PHASE': {
      if (!state) return state;
      return { ...state, phase: action.phase };
    }

    case 'MINIGAME_STARTED': {
      if (!state) return state;
      return { ...state, minigameState: action.minigame };
    }

    case 'MINIGAME_ANSWER_COUNT': {
      if (!state?.minigameState) return state;
      return {
        ...state,
        minigameState: {
          ...state.minigameState,
          answeredCount: action.answeredCount,
          totalCount: action.totalCount,
        },
      };
    }

    case 'MINIGAME_RESULTS': {
      if (!state) return state;
      const updatedPlayers = action.updatedPlayers.map(snapshotToPlayer);
      const players = state.players.map(p => {
        const updated = updatedPlayers.find(u => u.id === p.id);
        return updated ?? p;
      });
      return { ...state, players, minigameState: action.minigame };
    }

    case 'MINIGAME_ENDED':
      return { ...snapshotToLobby(action.lobby), spinConfig: null };

    case 'ERROR':
      return state;

    default:
      return state;
  }
}

// ─── useLobbySocket ───────────────────────────────────────────────────────────

export function useLobbySocket(lobbyId: string, sessionToken: string | null) {
  const { socket } = useSocket();
  const [lobby, dispatch] = useReducer(lobbyReducer, null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const spinTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!socket || !lobbyId || !sessionToken) return;

    const join = () => socket.emit(CLIENT_EVENTS.JOIN_LOBBY, { lobbyId, sessionToken });
    socket.on('connect', join);
    if (socket.connected) join();

    const handleLobbyJoined = (payload: LobbyJoinedPayload) =>
      dispatch({ type: 'LOBBY_JOINED', lobby: payload.lobby });

    const handlePlayerUpdated = (payload: PlayerUpdatedPayload) =>
      dispatch({ type: 'PLAYER_UPDATED', player: payload.player });

    const handlePlayerLeft = (payload: PlayerLeftPayload) =>
      dispatch({ type: 'PLAYER_LEFT', playerId: payload.playerId });

    const handleCountdownStarted = () =>
      dispatch({ type: 'SET_PHASE', phase: 'countdown' });

    const handleSpinStarted = (payload: SpinStartedPayload) => {
      dispatch({ type: 'SPIN_STARTED', winner: payload.winner, finalAngle: payload.finalAngle, duration: payload.duration });
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
      spinTimerRef.current = setTimeout(() => dispatch({ type: 'SPIN_COMPLETE' }), payload.duration + 500);
    };

    const handleGameReset = (payload: GameResetPayload) =>
      dispatch({ type: 'GAME_RESET', lobby: payload.lobby });

    const handleAdminTransferred = (payload: AdminTransferredPayload) =>
      dispatch({ type: 'ADMIN_TRANSFERRED', newAdminId: payload.newAdminId });

    const handleError = (payload: ErrorPayload) =>
      setError({ code: payload.code, message: payload.message });

    const handleMinigameStarted = (payload: MinigameStartedPayload) =>
      dispatch({ type: 'MINIGAME_STARTED', minigame: payload.minigame });

    const handleMinigameAnswerReceived = (payload: MinigameAnswerReceivedPayload) =>
      dispatch({ type: 'MINIGAME_ANSWER_COUNT', answeredCount: payload.answeredCount, totalCount: payload.totalCount });

    const handleMinigameResults = (payload: MinigameResultsPayload) =>
      dispatch({ type: 'MINIGAME_RESULTS', minigame: payload.minigame, updatedPlayers: payload.updatedPlayers });

    const handleMinigameEnded = (payload: MinigameEndedPayload) =>
      dispatch({ type: 'MINIGAME_ENDED', lobby: payload.lobby });

    socket.on(SERVER_EVENTS.LOBBY_JOINED, handleLobbyJoined);
    socket.on(SERVER_EVENTS.PLAYER_UPDATED, handlePlayerUpdated);
    socket.on(SERVER_EVENTS.PLAYER_LEFT, handlePlayerLeft);
    socket.on(SERVER_EVENTS.COUNTDOWN_STARTED, handleCountdownStarted);
    socket.on(SERVER_EVENTS.SPIN_STARTED, handleSpinStarted);
    socket.on(SERVER_EVENTS.GAME_RESET, handleGameReset);
    socket.on(SERVER_EVENTS.ADMIN_TRANSFERRED, handleAdminTransferred);
    socket.on(SERVER_EVENTS.ERROR, handleError);
    socket.on(SERVER_EVENTS.MINIGAME_STARTED, handleMinigameStarted);
    socket.on(SERVER_EVENTS.MINIGAME_ANSWER_RECEIVED, handleMinigameAnswerReceived);
    socket.on(SERVER_EVENTS.MINIGAME_RESULTS, handleMinigameResults);
    socket.on(SERVER_EVENTS.MINIGAME_ENDED, handleMinigameEnded);

    return () => {
      socket.off('connect', join);
      socket.off(SERVER_EVENTS.LOBBY_JOINED, handleLobbyJoined);
      socket.off(SERVER_EVENTS.PLAYER_UPDATED, handlePlayerUpdated);
      socket.off(SERVER_EVENTS.PLAYER_LEFT, handlePlayerLeft);
      socket.off(SERVER_EVENTS.COUNTDOWN_STARTED, handleCountdownStarted);
      socket.off(SERVER_EVENTS.SPIN_STARTED, handleSpinStarted);
      socket.off(SERVER_EVENTS.GAME_RESET, handleGameReset);
      socket.off(SERVER_EVENTS.ADMIN_TRANSFERRED, handleAdminTransferred);
      socket.off(SERVER_EVENTS.ERROR, handleError);
      socket.off(SERVER_EVENTS.MINIGAME_STARTED, handleMinigameStarted);
      socket.off(SERVER_EVENTS.MINIGAME_ANSWER_RECEIVED, handleMinigameAnswerReceived);
      socket.off(SERVER_EVENTS.MINIGAME_RESULTS, handleMinigameResults);
      socket.off(SERVER_EVENTS.MINIGAME_ENDED, handleMinigameEnded);
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    };
  }, [socket, lobbyId, sessionToken]);

  const setupPlayer = useCallback(
    (name: string, avatarType: 'bundled' | 'custom', avatar: string) =>
      socket?.emit(CLIENT_EVENTS.SETUP_PLAYER, { lobbyId, name, avatarType, avatar }),
    [socket, lobbyId]
  );

  const updateCoefficient = useCallback(
    (targetPlayerId: string, coefficient: number) =>
      socket?.emit(CLIENT_EVENTS.UPDATE_COEFFICIENT, { lobbyId, targetPlayerId, coefficient }),
    [socket, lobbyId]
  );

  const startSpin = useCallback(() => {
    socket?.emit(CLIENT_EVENTS.START_SPIN, { lobbyId });
  }, [socket, lobbyId]);

  const resetGame = useCallback(() => {
    socket?.emit(CLIENT_EVENTS.RESET_GAME, { lobbyId });
  }, [socket, lobbyId]);

  const leaveLobby = useCallback(() => {
    socket?.emit(CLIENT_EVENTS.LEAVE_LOBBY, { lobbyId });
  }, [socket, lobbyId]);

  const launchMinigame = useCallback(
    (gameType: MinigameType, questionId?: string) =>
      socket?.emit(CLIENT_EVENTS.LAUNCH_MINIGAME, { lobbyId, gameType, questionId }),
    [socket, lobbyId]
  );

  const submitMinigameAnswer = useCallback(
    (answer: number | string) =>
      socket?.emit(CLIENT_EVENTS.SUBMIT_MINIGAME_ANSWER, { lobbyId, answer }),
    [socket, lobbyId]
  );

  const closeMinigameResults = useCallback(() => {
    socket?.emit(CLIENT_EVENTS.CLOSE_MINIGAME_RESULTS, { lobbyId });
  }, [socket, lobbyId]);

  const currentPlayer = lobby?.players.find(p => p.id === sessionToken) ?? null;
  const isAdmin = currentPlayer?.isAdmin ?? false;

  return {
    lobby, error, currentPlayer, isAdmin,
    setupPlayer, updateCoefficient, startSpin, resetGame, leaveLobby,
    launchMinigame, submitMinigameAnswer, closeMinigameResults,
  };
}

// ─── useLobbyCreate ───────────────────────────────────────────────────────────

export function useLobbyCreate() {
  const { socket, sessionToken } = useSocket();
  const [createdLobbyId, setCreatedLobbyId] = useState<string | null>(null);

  const createLobby = useCallback(() => {
    socket?.emit(CLIENT_EVENTS.CREATE_LOBBY, { sessionToken });
  }, [socket, sessionToken]);

  useEffect(() => {
    if (!socket) return;
    const handler = ({ lobbyId }: { lobbyId: string }) => setCreatedLobbyId(lobbyId);
    socket.on(SERVER_EVENTS.LOBBY_CREATED, handler);
    return () => { socket.off(SERVER_EVENTS.LOBBY_CREATED, handler); };
  }, [socket]);

  return { createLobby, createdLobbyId };
}
